import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  FileText, AlertTriangle, AlertCircle, CheckCircle, Clock,
  User, Phone, Play, Download, Mic, ChevronRight, RefreshCw,
  Plus, Search, Filter, Calendar, Activity, Heart, Pill,
  MessageSquare, Shield, PenLine, Eye, PhoneForwarded, Loader2,
  FileAudio, Languages, RotateCw
} from "lucide-react";

const SEVERITY_CONFIG = {
  critical: { color: "bg-red-500", textColor: "text-red-700", bgColor: "bg-red-50", label: "Critical" },
  high: { color: "bg-orange-500", textColor: "text-orange-700", bgColor: "bg-orange-50", label: "High" },
  normal: { color: "bg-blue-500", textColor: "text-blue-700", bgColor: "bg-blue-50", label: "Normal" },
  low: { color: "bg-gray-500", textColor: "text-gray-700", bgColor: "bg-gray-50", label: "Low" },
};

const ENTRY_TYPE_ICONS = {
  assessment: Activity,
  note: FileText,
  intervention: Shield,
  medication: Pill,
  vital_sign: Heart,
  alert: AlertTriangle,
};

export default function EHRDashboard() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("alerts");
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({});
  const [recordings, setRecordings] = useState([]);
  const [searchPatient, setSearchPatient] = useState("");

  // Dialogs
  const [showNewEntryDialog, setShowNewEntryDialog] = useState(false);
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientEntries, setPatientEntries] = useState([]);
  const [patientSummary, setPatientSummary] = useState(null);

  // Form state
  const [newEntry, setNewEntry] = useState({
    patient_id: "",
    entry_type: "note",
    category: "general",
    title: "",
    content: "",
    severity: "normal"
  });
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [createEhrOnResolve, setCreateEhrOnResolve] = useState(true);
  
  // Transcription state
  const [showTranscriptionDialog, setShowTranscriptionDialog] = useState(false);
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [transcriptionData, setTranscriptionData] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  
  // Stat dialogs
  const [showCriticalAlertsDialog, setShowCriticalAlertsDialog] = useState(false);
  const [showActiveAlertsDialog, setShowActiveAlertsDialog] = useState(false);
  const [showEntriesDialog, setShowEntriesDialog] = useState(false);
  const [showRecordingsDialog, setShowRecordingsDialog] = useState(false);
  const [showSignaturesDialog, setShowSignaturesDialog] = useState(false);
  const [pendingItems, setPendingItems] = useState([]);

  const isStaff = user?.role && ["admin", "administrator", "staff", "nurse", "coordinator", "physician"].includes(user.role?.toLowerCase());

  useEffect(() => {
    if (authLoading) return;
    
    if (!isStaff) {
      toast.error("Staff access required");
      navigate("/");
      return;
    }
    
    loadData();
  }, [user, authLoading, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [alertsRes, statsRes, recordingsRes] = await Promise.all([
        api.get("/ehr/alerts?status=active").catch(() => ({ data: { alerts: [] } })),
        api.get("/ehr/dashboard/stats").catch(() => ({ data: { stats: {} } })),
        api.get("/ehr/recordings?limit=20").catch(() => ({ data: { recordings: [] } }))
      ]);
      
      setAlerts(alertsRes.data.alerts || []);
      setStats(statsRes.data.stats || {});
      setRecordings(recordingsRes.data.recordings || []);
    } catch (error) {
      console.error("Error loading EHR data:", error);
      toast.error("Failed to load EHR data");
    } finally {
      setLoading(false);
    }
  };

  const loadPatientData = async (patientId) => {
    try {
      const [entriesRes, summaryRes] = await Promise.all([
        api.get(`/ehr/entries/patient/${patientId}?limit=50`),
        api.get(`/ehr/summary/patient/${patientId}`)
      ]);
      
      setPatientEntries(entriesRes.data.entries || []);
      setPatientSummary(summaryRes.data);
      setSelectedPatient(patientId);
    } catch (error) {
      console.error("Error loading patient data:", error);
      toast.error("Failed to load patient records");
    }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      await api.post(`/ehr/alerts/${alertId}/acknowledge`, {});
      toast.success("Alert acknowledged");
      loadData();
    } catch (error) {
      toast.error("Failed to acknowledge alert");
    }
  };

  const resolveAlert = async () => {
    if (!selectedAlert) return;
    
    try {
      await api.post(`/ehr/alerts/${selectedAlert.id}/resolve`, {
        resolution_notes: resolutionNotes,
        create_ehr_entry: createEhrOnResolve
      });
      toast.success("Alert resolved" + (createEhrOnResolve ? " and documented in EHR" : ""));
      setShowResolveDialog(false);
      setSelectedAlert(null);
      setResolutionNotes("");
      loadData();
    } catch (error) {
      toast.error("Failed to resolve alert");
    }
  };

  const escalateToTriage = async (alertId) => {
    try {
      await api.post(`/ehr/alerts/${alertId}/escalate-to-triage`);
      toast.success("Alert escalated to triage queue");
      loadData();
    } catch (error) {
      toast.error("Failed to escalate to triage");
    }
  };

  const createEntry = async () => {
    if (!newEntry.patient_id || !newEntry.title || !newEntry.content) {
      toast.error("Please fill all required fields");
      return;
    }
    
    try {
      await api.post("/ehr/entries", newEntry);
      toast.success("EHR entry created");
      setShowNewEntryDialog(false);
      setNewEntry({
        patient_id: "",
        entry_type: "note",
        category: "general",
        title: "",
        content: "",
        severity: "normal"
      });
      if (selectedPatient) {
        loadPatientData(selectedPatient);
      }
    } catch (error) {
      toast.error("Failed to create entry");
    }
  };

  const signEntry = async (entryId) => {
    try {
      await api.post(`/ehr/entries/${entryId}/sign`);
      toast.success("Entry signed");
      if (selectedPatient) {
        loadPatientData(selectedPatient);
      }
    } catch (error) {
      toast.error("Failed to sign entry");
    }
  };

  const viewTranscription = async (recording) => {
    setSelectedRecording(recording);
    setShowTranscriptionDialog(true);
    
    try {
      const res = await api.get(`/ehr/recordings/${recording.id}/transcription`);
      setTranscriptionData(res.data);
    } catch (error) {
      console.error("Failed to load transcription:", error);
      setTranscriptionData({ status: "error", error: "Failed to load transcription" });
    }
  };

  const triggerTranscription = async (recordingId) => {
    setTranscribing(true);
    try {
      await api.post(`/ehr/recordings/${recordingId}/transcribe`);
      toast.success("Transcription started! This may take a minute.");
      // Refresh the transcription data
      setTimeout(async () => {
        const res = await api.get(`/ehr/recordings/${recordingId}/transcription`);
        setTranscriptionData(res.data);
        setTranscribing(false);
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start transcription");
      setTranscribing(false);
    }
  };

  const refreshTranscription = async (recordingId) => {
    try {
      const res = await api.get(`/ehr/recordings/${recordingId}/transcription`);
      setTranscriptionData(res.data);
      if (res.data.status === "completed") {
        toast.success("Transcription completed!");
        loadData(); // Refresh recordings list
      }
    } catch (error) {
      console.error("Failed to refresh transcription:", error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="ehr-dashboard">
      <div className="container-max px-6 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-serif font-medium text-foreground flex items-center gap-3">
              <FileText className="w-8 h-8 text-primary" />
              EHR Documentation
            </h1>
            <p className="text-muted-foreground mt-1">
              Patient alerts, documentation, and call recordings
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => setShowNewEntryDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              New Entry
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card 
            className={`cursor-pointer hover:shadow-lg transition-shadow ${stats.critical_alerts > 0 ? "border-red-300 bg-red-50" : ""}`}
            onClick={() => {
              setPendingItems(alerts.filter(a => a.severity === 'critical'));
              setShowCriticalAlertsDialog(true);
            }}
            data-testid="stat-critical-alerts"
          >
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-red-600">{stats.critical_alerts || 0}</p>
                  <p className="text-sm text-muted-foreground">Critical Alerts</p>
                </div>
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>
              <p className="text-xs text-red-500 mt-2">View critical →</p>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => {
              setPendingItems(alerts.filter(a => a.status === 'active'));
              setShowActiveAlertsDialog(true);
            }}
            data-testid="stat-active-alerts"
          >
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold">{stats.active_alerts || 0}</p>
                  <p className="text-sm text-muted-foreground">Active Alerts</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-amber-500" />
              </div>
              <p className="text-xs text-amber-500 mt-2">View active →</p>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setShowEntriesDialog(true)}
            data-testid="stat-entries-today"
          >
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold">{stats.ehr_entries_today || 0}</p>
                  <p className="text-sm text-muted-foreground">Entries Today</p>
                </div>
                <FileText className="w-8 h-8 text-blue-500" />
              </div>
              <p className="text-xs text-blue-500 mt-2">View entries →</p>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setShowRecordingsDialog(true)}
            data-testid="stat-recordings"
          >
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold">{stats.recordings_today || 0}</p>
                  <p className="text-sm text-muted-foreground">Recordings</p>
                </div>
                <Mic className="w-8 h-8 text-purple-500" />
              </div>
              <p className="text-xs text-purple-500 mt-2">View recordings →</p>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setShowSignaturesDialog(true)}
            data-testid="stat-signatures"
          >
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold">{stats.pending_signatures || 0}</p>
                  <p className="text-sm text-muted-foreground">Pending Signatures</p>
                </div>
                <PenLine className="w-8 h-8 text-green-500" />
              </div>
              <p className="text-xs text-green-500 mt-2">View pending →</p>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="alerts" className="gap-2">
              <AlertTriangle className="w-4 h-4" />
              Patient Alerts
              {alerts.length > 0 && (
                <Badge variant="destructive" className="ml-1">{alerts.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="recordings" className="gap-2">
              <Mic className="w-4 h-4" />
              Call Recordings
            </TabsTrigger>
            <TabsTrigger value="patient" className="gap-2">
              <User className="w-4 h-4" />
              Patient Records
            </TabsTrigger>
          </TabsList>

          {/* Alerts Tab */}
          <TabsContent value="alerts">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Active Patient Alerts</span>
                  <Badge variant="outline">{alerts.length} alerts</Badge>
                </CardTitle>
                <CardDescription>
                  High patient needs requiring attention or documentation
                </CardDescription>
              </CardHeader>
              <CardContent>
                {alerts.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500 opacity-50" />
                    <p className="text-lg font-medium">No Active Alerts</p>
                    <p className="text-sm mt-1">All patient alerts have been addressed</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[500px]">
                    <div className="space-y-4">
                      {alerts.map((alert) => (
                        <div 
                          key={alert.id}
                          className={`p-4 rounded-lg border-2 ${
                            alert.severity === "critical" ? "border-red-300 bg-red-50" :
                            alert.severity === "high" ? "border-orange-300 bg-orange-50" :
                            "border-border"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge className={`${SEVERITY_CONFIG[alert.severity]?.color} text-white`}>
                                  {alert.severity?.toUpperCase()}
                                </Badge>
                                <Badge variant="outline">{alert.alert_type}</Badge>
                                <span className="text-xs text-muted-foreground">
                                  <Clock className="w-3 h-3 inline mr-1" />
                                  {formatDate(alert.created_at)}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 mb-1">
                                <User className="w-4 h-4 text-muted-foreground" />
                                <button 
                                  onClick={() => loadPatientData(alert.patient_id)}
                                  className="font-semibold text-primary hover:underline"
                                >
                                  {alert.patient_name}
                                </button>
                              </div>
                              <p className="font-medium">{alert.title}</p>
                              <p className="text-sm text-muted-foreground mt-1">{alert.description}</p>
                              
                              {alert.ehr_entry_created && (
                                <Badge variant="secondary" className="mt-2 text-xs">
                                  <FileText className="w-3 h-3 mr-1" />
                                  EHR Entry Created
                                </Badge>
                              )}
                              {alert.triage_call_created && (
                                <Badge variant="secondary" className="mt-2 ml-2 text-xs">
                                  <Phone className="w-3 h-3 mr-1" />
                                  Triage Call Created
                                </Badge>
                              )}
                            </div>
                            <div className="flex flex-col gap-2">
                              {alert.status === "active" && (
                                <>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => acknowledgeAlert(alert.id)}
                                  >
                                    <Eye className="w-4 h-4 mr-1" />
                                    Acknowledge
                                  </Button>
                                  <Button 
                                    size="sm"
                                    onClick={() => {
                                      setSelectedAlert(alert);
                                      setShowResolveDialog(true);
                                    }}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Resolve
                                  </Button>
                                  {alert.severity === "critical" && !alert.triage_call_created && (
                                    <Button 
                                      size="sm" 
                                      variant="destructive"
                                      onClick={() => escalateToTriage(alert.id)}
                                    >
                                      <PhoneForwarded className="w-4 h-4 mr-1" />
                                      Escalate
                                    </Button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recordings Tab */}
          <TabsContent value="recordings">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileAudio className="w-5 h-5" />
                  Call Recordings with AI Transcription
                </CardTitle>
                <CardDescription>
                  Recorded telehealth and triage calls with AI-powered transcription for searchable content
                </CardDescription>
              </CardHeader>
              <CardContent>
                {recordings.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Mic className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg">No Recordings Yet</p>
                    <p className="text-sm mt-1">Call recordings will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recordings.map((rec) => (
                      <div key={rec.id} className="p-4 border rounded-lg hover:bg-muted/30 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                              <Mic className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium">{rec.patient_name}</p>
                              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                                <span className="capitalize">{rec.call_type}</span>
                                <span>•</span>
                                <span>{formatDuration(rec.duration_seconds)}</span>
                                <span>•</span>
                                <span>{formatDate(rec.created_at)}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{rec.status}</Badge>
                            {/* Transcription Status Badge */}
                            {rec.transcription_status && (
                              <Badge 
                                variant={
                                  rec.transcription_status === "completed" ? "default" :
                                  rec.transcription_status === "processing" ? "secondary" :
                                  rec.transcription_status === "failed" ? "destructive" : "outline"
                                }
                                className="gap-1"
                              >
                                {rec.transcription_status === "completed" && <Languages className="w-3 h-3" />}
                                {rec.transcription_status === "processing" && <Loader2 className="w-3 h-3 animate-spin" />}
                                {rec.transcription_status === "completed" ? "Transcribed" :
                                 rec.transcription_status === "processing" ? "Transcribing..." :
                                 rec.transcription_status === "failed" ? "Failed" : "Pending"}
                              </Badge>
                            )}
                            {rec.status === "ready" && (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => viewTranscription(rec)}
                                  data-testid={`view-transcription-${rec.id}`}
                                >
                                  <Languages className="w-4 h-4 mr-1" />
                                  Transcript
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => window.open(`/api/ehr/recordings/${rec.id}/download`, '_blank')}
                                  data-testid={`download-recording-${rec.id}`}
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                        {/* Transcription Preview */}
                        {rec.transcription && (
                          <div className="mt-3 p-3 bg-muted/50 rounded-md">
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              <span className="font-medium text-foreground">Transcript: </span>
                              {rec.transcription}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Patient Records Tab */}
          <TabsContent value="patient">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Patient Search */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Search Patient</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Patient ID or name..."
                        value={searchPatient}
                        onChange={(e) => setSearchPatient(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                    <Button 
                      className="w-full"
                      onClick={() => searchPatient && loadPatientData(searchPatient)}
                      disabled={!searchPatient}
                    >
                      <Search className="w-4 h-4 mr-2" />
                      Search
                    </Button>
                  </div>
                  
                  {patientSummary && (
                    <div className="mt-6 pt-6 border-t">
                      <h4 className="font-medium mb-3">Patient Summary</h4>
                      <div className="space-y-2 text-sm">
                        <p><strong>Name:</strong> {patientSummary.patient?.name}</p>
                        <p><strong>Email:</strong> {patientSummary.patient?.email}</p>
                        <p><strong>Entries (30d):</strong> {patientSummary.summary?.ehr_entries}</p>
                        <p><strong>Alerts (30d):</strong> {patientSummary.summary?.alerts}</p>
                        <p><strong>Recordings:</strong> {patientSummary.summary?.recordings}</p>
                      </div>
                      
                      {patientSummary.active_alerts?.length > 0 && (
                        <div className="mt-4">
                          <p className="text-sm font-medium text-red-600">
                            {patientSummary.active_alerts.length} Active Alerts
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Patient Entries */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>EHR Entries</span>
                    {selectedPatient && (
                      <Button 
                        size="sm"
                        onClick={() => {
                          setNewEntry(prev => ({ ...prev, patient_id: selectedPatient }));
                          setShowNewEntryDialog(true);
                        }}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Entry
                      </Button>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {patientEntries.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No entries found</p>
                      <p className="text-sm mt-1">Search for a patient to view their records</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-[400px]">
                      <div className="space-y-3">
                        {patientEntries.map((entry) => {
                          const Icon = ENTRY_TYPE_ICONS[entry.entry_type] || FileText;
                          return (
                            <div key={entry.id} className="p-4 border rounded-lg">
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start gap-3">
                                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                    SEVERITY_CONFIG[entry.severity]?.bgColor || "bg-muted"
                                  }`}>
                                    <Icon className={`w-5 h-5 ${SEVERITY_CONFIG[entry.severity]?.textColor || ""}`} />
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="font-medium">{entry.title}</span>
                                      <Badge variant="outline" className="text-xs">{entry.entry_type}</Badge>
                                      {entry.signed && (
                                        <Badge className="bg-green-500 text-xs">Signed</Badge>
                                      )}
                                    </div>
                                    <p className="text-sm text-muted-foreground">{entry.content}</p>
                                    <p className="text-xs text-muted-foreground mt-2">
                                      {entry.created_by_name} • {formatDate(entry.created_at)}
                                    </p>
                                  </div>
                                </div>
                                {!entry.signed && (
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => signEntry(entry.id)}
                                  >
                                    <PenLine className="w-4 h-4 mr-1" />
                                    Sign
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* New Entry Dialog */}
        <Dialog open={showNewEntryDialog} onOpenChange={setShowNewEntryDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create EHR Entry</DialogTitle>
              <DialogDescription>
                Document patient information in the electronic health record
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Patient ID *</Label>
                <Input
                  value={newEntry.patient_id}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, patient_id: e.target.value }))}
                  placeholder="Enter patient ID"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Entry Type</Label>
                  <Select 
                    value={newEntry.entry_type} 
                    onValueChange={(v) => setNewEntry(prev => ({ ...prev, entry_type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="note">Note</SelectItem>
                      <SelectItem value="assessment">Assessment</SelectItem>
                      <SelectItem value="intervention">Intervention</SelectItem>
                      <SelectItem value="medication">Medication</SelectItem>
                      <SelectItem value="vital_sign">Vital Sign</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Severity</Label>
                  <Select 
                    value={newEntry.severity} 
                    onValueChange={(v) => setNewEntry(prev => ({ ...prev, severity: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={newEntry.title}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Entry title"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Content *</Label>
                <Textarea
                  value={newEntry.content}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Document the entry details..."
                  rows={5}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewEntryDialog(false)}>
                Cancel
              </Button>
              <Button onClick={createEntry}>
                <Plus className="w-4 h-4 mr-2" />
                Create Entry
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Resolve Alert Dialog */}
        <Dialog open={showResolveDialog} onOpenChange={setShowResolveDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Resolve Alert</DialogTitle>
              <DialogDescription>
                Document the resolution of this patient alert
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {selectedAlert && (
                <div className="p-4 bg-muted rounded-lg">
                  <p className="font-medium">{selectedAlert.title}</p>
                  <p className="text-sm text-muted-foreground">{selectedAlert.patient_name}</p>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Resolution Notes *</Label>
                <Textarea
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  placeholder="Document how the alert was resolved..."
                  rows={4}
                />
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="createEhr"
                  checked={createEhrOnResolve}
                  onChange={(e) => setCreateEhrOnResolve(e.target.checked)}
                  className="rounded"
                />
                <Label htmlFor="createEhr" className="text-sm font-normal">
                  Create EHR entry for this resolution
                </Label>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowResolveDialog(false)}>
                Cancel
              </Button>
              <Button onClick={resolveAlert} disabled={!resolutionNotes}>
                <CheckCircle className="w-4 h-4 mr-2" />
                Resolve Alert
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Transcription Dialog */}
        <Dialog open={showTranscriptionDialog} onOpenChange={setShowTranscriptionDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Languages className="w-5 h-5 text-primary" />
                Call Transcription
              </DialogTitle>
              <DialogDescription>
                {selectedRecording && (
                  <span>
                    {selectedRecording.patient_name} • {selectedRecording.call_type} call • {formatDuration(selectedRecording.duration_seconds)}
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              {!transcriptionData ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : transcriptionData.status === "completed" ? (
                <ScrollArea className="h-[400px]">
                  <div className="prose prose-sm max-w-none">
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <p className="whitespace-pre-wrap text-foreground leading-relaxed">
                        {transcriptionData.transcription}
                      </p>
                    </div>
                  </div>
                </ScrollArea>
              ) : transcriptionData.status === "processing" ? (
                <div className="text-center py-12">
                  <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
                  <p className="text-lg font-medium">Transcription in Progress</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    AI is converting audio to text. This may take 1-2 minutes.
                  </p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => selectedRecording && refreshTranscription(selectedRecording.id)}
                  >
                    <RotateCw className="w-4 h-4 mr-2" />
                    Check Status
                  </Button>
                </div>
              ) : transcriptionData.status === "failed" ? (
                <div className="text-center py-12">
                  <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
                  <p className="text-lg font-medium text-destructive">Transcription Failed</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {transcriptionData.error || "An error occurred during transcription"}
                  </p>
                  <Button 
                    className="mt-4"
                    onClick={() => selectedRecording && triggerTranscription(selectedRecording.id)}
                    disabled={transcribing}
                  >
                    {transcribing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RotateCw className="w-4 h-4 mr-2" />
                    )}
                    Retry Transcription
                  </Button>
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileAudio className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-lg font-medium">No Transcription Available</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Click below to generate an AI transcription of this call
                  </p>
                  <Button 
                    className="mt-4"
                    onClick={() => selectedRecording && triggerTranscription(selectedRecording.id)}
                    disabled={transcribing}
                  >
                    {transcribing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Languages className="w-4 h-4 mr-2" />
                    )}
                    Generate Transcription
                  </Button>
                </div>
              )}
            </div>

            <DialogFooter className="flex justify-between">
              <div>
                {transcriptionData?.status === "completed" && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(transcriptionData.transcription);
                      toast.success("Transcription copied to clipboard");
                    }}
                  >
                    Copy to Clipboard
                  </Button>
                )}
              </div>
              <Button variant="outline" onClick={() => {
                setShowTranscriptionDialog(false);
                setSelectedRecording(null);
                setTranscriptionData(null);
              }}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
